import { z } from 'zod';

export const AuthPreflightTypeSchema = z.enum([
  'HTTP_PROBE',
  'SHARED_LOGIN',
  'MULTIPLE_LOGINS',
]);

export const AuthPreflightTaskStatusSchema = z.enum([
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
]);

export const AuthPreflightResultSchema = z.enum([
  'ALLOWED',
  'BLOCKED',
  'EVICTED',
]);

export const AuthPreflightFailureReasonSchema = z.enum([
  'LOGIN_UNUSABLE',
  'AUTHENTICATION_FAILED',
  'INCONCLUSIVE',
  'BROWSER_UNAVAILABLE',
  'TIMED_OUT',
  'ABANDONED',
  'BUSY',
  'FEATURE_DISABLED',
  'NOT_APPLICABLE',
  'ERROR',
]);

export const AuthPreflightSharedLoginSchema = z.enum([
  'UNKNOWN',
  'ALLOWED',
  'BLOCKED',
]);

export const AuthPreflightHttpProbeSchema = z.enum([
  'UNKNOWN',
  'READY',
  'UNAVAILABLE',
]);

export const ApplicationCredentialAuthPreflightResultsSchema = z.object({
  sharedLogin: AuthPreflightSharedLoginSchema.default('UNKNOWN'),
  httpProbe: AuthPreflightHttpProbeSchema.default('UNKNOWN'),
});

export const AuthPreflightSucceededOutcomeSchema = z.object({
  status: z.literal('SUCCEEDED'),
  result: AuthPreflightResultSchema,
  reason: z.string().trim().min(1).max(500),
});

export const AuthPreflightFailedOutcomeSchema = z.object({
  status: z.literal('FAILED'),
  failureReason: AuthPreflightFailureReasonSchema,
  reason: z.string().trim().min(1).max(500),
});

export const AuthPreflightOutcomeSchema = z.discriminatedUnion('status', [
  AuthPreflightSucceededOutcomeSchema,
  AuthPreflightFailedOutcomeSchema,
]);

export type AuthPreflightType = z.infer<typeof AuthPreflightTypeSchema>;
export type AuthPreflightTaskStatus = z.infer<
  typeof AuthPreflightTaskStatusSchema
>;
export type AuthPreflightResult = z.infer<typeof AuthPreflightResultSchema>;
export type AuthPreflightFailureReason = z.infer<
  typeof AuthPreflightFailureReasonSchema
>;
export type AuthPreflightSharedLogin = z.infer<
  typeof AuthPreflightSharedLoginSchema
>;
export type AuthPreflightHttpProbe = z.infer<
  typeof AuthPreflightHttpProbeSchema
>;
export type ApplicationCredentialAuthPreflightResults = z.infer<
  typeof ApplicationCredentialAuthPreflightResultsSchema
>;
export type AuthPreflightOutcome = z.infer<typeof AuthPreflightOutcomeSchema>;

export const SHARED_LOGIN_PREFLIGHT_RESULTS = [
  'ALLOWED',
  'BLOCKED',
] as const satisfies readonly AuthPreflightResult[];

export const isSharedLoginPreflightResult = (
  result: AuthPreflightResult,
): result is (typeof SHARED_LOGIN_PREFLIGHT_RESULTS)[number] =>
  SHARED_LOGIN_PREFLIGHT_RESULTS.some((allowed) => allowed === result);

export const MULTIPLE_LOGINS_PREFLIGHT_RESULTS = [
  'ALLOWED',
  'EVICTED',
] as const satisfies readonly AuthPreflightResult[];

export const isMultipleLoginsPreflightResult = (
  result: AuthPreflightResult,
): result is (typeof MULTIPLE_LOGINS_PREFLIGHT_RESULTS)[number] =>
  MULTIPLE_LOGINS_PREFLIGHT_RESULTS.some((allowed) => allowed === result);
